
import cx from 'classnames'
import { parseISO, isValid, isSameDay, subDays } from 'date-fns'
import * as fns from 'date-fns/format'
import React, { useEffect, useState } from 'react'

import { useAppSelector } from '../../../store'

interface VersionInfoProps {
  module?: string
  date?: string
  commit?: string
}

function VersionInfo ({ commit, module, ...props }: VersionInfoProps): JSX.Element | null {
  let date = props.date?.trim() ? parseISO(props.date.trim()) : undefined

  if (!commit) {
    return null
  }

  return (
    <div className='text-sm'>
      {typeof module === 'string' && (
        <span className='font-inter'>
          {module}:{' '}
        </span>
      )}

      <a
        className={cx(commit !== 'local' && 'hover:underline underline-offset-4')}
        href={commit === 'local' ? undefined : `https://bitbucket.org/caremanagment/narus-web/commits/${commit}`}
        rel='noopener noreferrer'
        target={"_blank"}
      >
        <code>
          {commit.substring(0, 7)}
        </code>
      </a>

      {date && isValid(date) && (
        <>
          {', '}

          <button
            type='button'
            title={fns.format(date, 'dd/MM/yyyy')}
            className='cursor-pointer p-1 -m-1'
            onClick={() => {
              invariant(date, 'date is not defined')

              void navigator.clipboard.writeText(
                `${fns.format(date, 'yyyy-MM-dd')} | ${commit.substring(0, 7)}`
              )
            }}
          >
            {humanReadableDifferenceFromNow(date)}
          </button>
        </>
      )}
    </div>
  )
}

interface VersionProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'center' | 'right'
}

export function Version ({ className, align }: VersionProps): JSX.Element | null {

  let { client, server } = useAppSelector(state => state.app)

  let showNothing = (
    !client.version.ref &&
    !server.version.ref
  )

  let showSingleVersion = (
    (client.version.ref && !server.version.ref) ||
    (!client.version.ref && server.version.ref) ||
    (client.version.ref === server.version.ref)
  )

  let showBothVersions = (
    client.version.ref &&
    server.version.ref &&
    client.version.ref !== server.version.ref
  )

  let showWarning = (
    client.version.ref &&
    server.version.ref &&
    client.version.ref !== server.version.ref
  )

  return (
    <div
      className='tailwind'
    >
      <div className={cx('flex flex-row p-4', className)}>
        <div className={cx('flex flex-col flex-grow gap-0.5')}>
          <div className='font-inter text-sm font-medium text-gray-700'>
            &copy;
            {' '}
            <span className='font-inter text-sm font-medium'>
              <Year />
            </span>
            {' '}
            Lucent Health Solutions, Inc
          </div>

          <div
            className={cx(
                'font-inter font-regular text-sm text-gray-500 gap-2',
              align === 'right' && 'justify-end',
              align === 'center' && 'justify-center'
            )}
          >
            {!showNothing && showSingleVersion && (
              <VersionInfo
                date={client.version.date ?? server.version.date}
                commit={client.version.ref ?? server.version.ref}
              />
            )}

            {!showNothing && showBothVersions && (
              <>
                <VersionInfo
                  module='client'
                  date={client.version.date}
                  commit={client.version.ref}
                />

                { }
                <span className='font-inter text-sm font-regular text-gray-400'>
                  //
                </span>

                <VersionInfo
                  module='server'
                  date={server.version.date}
                  commit={server.version.ref}
                />
              </>
            )}

            {showWarning && (
              <button
                type='button'
                className='text-sm text-inter font-semibold text-orange-500 hover:text-orange-600 animate-pulse'
                title='Version mismatch, click to refresh'
                onClick={() => {
                  window.location.reload()
                }}
              >
                Refresh
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Year (): JSX.Element {
  let [ year, setYear ] = useState(new Date().getFullYear())

  useEffect(() => {
    let interval = setInterval(() => {
      setYear(new Date().getFullYear())
    }, 60_000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <>
      {year}
    </>
  )
}

export function humanReadableDifferenceFromNow (date: Date): string {
  let now = new Date()
  let diff = Math.abs(now.getTime() - date.getTime())

  let isToday = isSameDay(now, date)
  let isYesterday = isSameDay(subDays(now, 1), date)

  let seconds = Math.floor(diff / 1000)
  let minutes = Math.floor(seconds / 60)
  let hours = Math.floor(minutes / 60)
  let days = Math.floor(hours / 24)
  let months = Math.floor(days / 30)
  let years = Math.floor(months / 12)

  if (isToday) {
    return 'today'
  }

  if (isYesterday) {
    return 'yesterday'
  }

  if (days < 30) {
    return `${days} days ago`
  }

  if (months === 1) {
    return '1 month ago'
  }

  if (months < 12) {
    return `${months} months ago`
  }

  if (years === 1) {
    return '1 year ago'
  }

  return `${years} years ago`
}

export function invariant (condition: unknown, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message)
  }
}
